import { Auth0Provider, type AppState } from "@auth0/auth0-react";
import { useNavigate } from "react-router-dom";
import type { ReactNode } from "react";
import { AUTH0_AUDIENCE, AUTH0_CLIENT_ID, AUTH0_CONNECTION, AUTH0_DOMAIN, AUTH_ENABLED } from "./config";

/**
 * Authorization Code + PKCE, per docs/production-service-decision-record.md's "Frontend"
 * decision — interactive human login for Staff+ engineers and product managers, as
 * distinct from the backend's own M2M client-credentials flow to other services.
 *
 * Runs unauthenticated (no provider at all) when Auth0 isn't configured — see
 * ./config.ts's AUTH_ENABLED. Every request the API client makes still works, just
 * with no bearer token, exactly like the backend's own laptop-mode fallback.
 */
export function AuthProvider({ children }: { children: ReactNode }) {
  const navigate = useNavigate();

  if (!AUTH_ENABLED) {
    if (import.meta.env.DEV) {
      console.warn("Auth0 is not configured (see .env.example) — running unauthenticated.");
    }
    return <>{children}</>;
  }

  /** After Auth0 redirects back, return the visitor to the page they were trying to open,
   * not the bare origin the redirect lands on. */
  const onRedirectCallback = (appState?: AppState) => {
    navigate(appState?.returnTo ?? window.location.pathname);
  };

  return (
    <Auth0Provider
      domain={AUTH0_DOMAIN}
      clientId={AUTH0_CLIENT_ID}
      authorizationParams={{
        redirect_uri: window.location.origin,
        audience: AUTH0_AUDIENCE,
        ...(AUTH0_CONNECTION ? { connection: AUTH0_CONNECTION } : {}),
      }}
      onRedirectCallback={onRedirectCallback}
    >
      {children}
    </Auth0Provider>
  );
}
